<template>
  <div class="w-full h-[600px] rounded-lg overflow-hidden shadow-lg relative">
    <div ref="mapContainer" class="w-full h-full"></div>
    
    <!-- Search bar -->
    <div class="absolute top-4 left-4 z-[1000] w-80">
      <div class="relative">
        <input
          v-model="searchQuery"
          @keyup.enter="searchLocation"
          type="text"
          placeholder="Buscar dirección..."
          class="w-full px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#18223B] pr-10"
        />
        <button
          @click="searchLocation"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#18223B]"
        >
          <Search :size="20" />
        </button>
      </div>
      
      <!-- Search results -->
      <div v-if="searchResults.length > 0" class="mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
        <div
          v-for="result in searchResults"
          :key="result.place_id"
          @click="selectLocation(result)"
          class="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
        >
          <p class="font-medium text-sm">{{ result.display_name }}</p>
        </div>
      </div>
    </div>

    <!-- Location info -->
    <div v-if="currentLocation" class="absolute top-20 left-4 bg-white px-3 py-2 rounded-md shadow-sm text-sm text-gray-600 z-[1000]">
      {{ currentLocation }}
    </div>

    <!-- Map controls -->
    <div class="absolute bottom-4 right-4 z-[1000] space-y-2">
      <button
        @click="toggleSatellite"
        class="bg-white px-4 py-2 rounded-md shadow-sm text-sm hover:bg-gray-50 transition-colors w-full"
      >
        {{ isSatellite ? 'Vista Mapa' : 'Vista Satélite' }}
      </button>
      
      <div class="bg-white rounded-md shadow-sm flex flex-col">
        <button
          @click="zoomIn"
          class="px-3 py-2 hover:bg-gray-50 transition-colors border-b"
        >
          <Plus :size="20" class="mx-auto" />
        </button>
        <button
          @click="zoomOut"
          class="px-3 py-2 hover:bg-gray-50 transition-colors"
        >
          <Minus :size="20" class="mx-auto" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { Camera } from '../types';
import { Search, Plus, Minus } from 'lucide-vue-next';
import { computed } from 'vue';

const props = defineProps<{
  cameras: Camera[];
  isAddingCamera: boolean;
}>();

const emit = defineEmits<{
  (e: 'camera-added', camera: Camera): void;
  (e: 'camera-moved', id: string, position: [number, number]): void;
  (e: 'target-moved', id: string, position: [number, number]): void;
}>();

const mapContainer = ref<HTMLElement | null>(null);
const map = ref<L.Map | null>(null);
const isSatellite = ref(true);
const drawingLayer = ref<L.FeatureGroup | null>(null);
const tileLayer = ref<L.TileLayer | null>(null);
const isMapInitialized = ref(false);
const cameraLayers = ref<Map<string, L.Layer[]>>(new Map());

// Search functionality
const searchQuery = ref('');
const searchResults = ref<any[]>([]);
const currentLocation = ref<string | null>(null);

const mapLayers = {
  standard: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
};

const cameraIcon = computed(() => new L.DivIcon({
  html: `<div class="flex items-center justify-center w-6 h-6 text-[#18223B]">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  </div>`,
  className: 'camera-icon',
  iconSize: [24, 24],
  iconAnchor: [12, 12]
}));

const targetIcon = computed(() => new L.DivIcon({
  html: `<div class="flex items-center justify-center w-6 h-6 text-[#F6A11B]">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="22" y1="12" x2="18" y2="12"/>
      <line x1="6" y1="12" x2="2" y2="12"/>
      <line x1="12" y1="6" x2="12" y2="2"/>
      <line x1="12" y1="22" x2="12" y2="18"/>
    </svg>
  </div>`,
  className: 'target-icon',
  iconSize: [24, 24],
  iconAnchor: [12, 12]
}));

async function searchLocation() {
  if (!searchQuery.value.trim()) return;
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery.value)}`
    );
    const data = await response.json();
    searchResults.value = data;
  } catch (error) {
    console.error('Error searching location:', error);
    searchResults.value = [];
  }
}

function selectLocation(result: any) {
  if (!map.value) return;
  
  const lat = parseFloat(result.lat);
  const lon = parseFloat(result.lon);
  
  map.value.setView([lat, lon], 18);
  currentLocation.value = result.display_name;
  searchResults.value = [];
  searchQuery.value = '';
}

function calculateVisionCone(camera: Camera): [number, number][] {
  const points: [number, number][] = [];
  const [lat1, lng1] = camera.position;
  const [lat2, lng2] = camera.target;
  
  // Calculate bearing using Haversine formula
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;
  
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const bearing = Math.atan2(y, x);
  
  // Calculate distance between camera and target
  const R = 6371000; // Earth's radius in meters
  const Δφ = (lat2-lat1) * Math.PI/180;
  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  // Convert distance to degrees (approximate)
  const distanceDeg = distance / (R * Math.PI / 180);
  
  const fovRad = (camera.fov * Math.PI) / 180;
  const segments = 32;
  
  // Calculate points for the vision cone up to target distance
  for (let i = 0; i <= segments; i++) {
    const angle = bearing - (fovRad / 2) + (fovRad * i / segments);
    const pointLat = lat1 + (distanceDeg * Math.cos(angle));
    const pointLng = lng1 + (distanceDeg * Math.sin(angle));
    points.push([pointLat, pointLng]);
  }
  
  // Return the cone points with camera position as first and last point
  return [camera.position, ...points, camera.position];
}

function toggleSatellite() {
  if (!map.value || !tileLayer.value) return;
  
  isSatellite.value = !isSatellite.value;
  tileLayer.value.setUrl(isSatellite.value ? mapLayers.satellite : mapLayers.standard);
}

function zoomIn() {
  if (!map.value) return;
  map.value.setZoom(map.value.getZoom() + 1);
}

function zoomOut() {
  if (!map.value) return;
  map.value.setZoom(map.value.getZoom() - 1);
}

let pendingCamera: Partial<Camera> | null = null;
let temporaryMarker: L.Marker | null = null;

function clearCameraLayers(cameraId: string) {
  const layers = cameraLayers.value.get(cameraId);
  if (layers) {
    layers.forEach(layer => {
      if (map.value) {
        map.value.removeLayer(layer);
      }
    });
    cameraLayers.value.delete(cameraId);
  }
}

async function initializeMap() {
  if (!mapContainer.value || map.value) return;

  // Initialize map with animation disabled initially
  map.value = L.map(mapContainer.value, {
    fadeAnimation: false,
    zoomAnimation: false,
    markerZoomAnimation: false,
    maxZoom: 22, // Aumentar el zoom máximo
    minZoom: 3
  });

  // Set initial view
  map.value.setView([41.3851, 2.1734], 18); // Aumentar el zoom inicial
  
  // Initialize tile layer with satellite view by default
  tileLayer.value = L.tileLayer(mapLayers.satellite, {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 22 // Asegurar que las tiles soporten el zoom máximo
  }).addTo(map.value);

  // Wait for the tile layer to load
  await new Promise<void>((resolve) => {
    if (tileLayer.value) {
      tileLayer.value.once('load', () => {
        resolve();
      });
    } else {
      resolve();
    }
  });

  // Enable animations after initial load
  if (map.value) {
    map.value.options.fadeAnimation = true;
    map.value.options.zoomAnimation = true;
    map.value.options.markerZoomAnimation = true;
  }

  // Initialize drawing layer
  drawingLayer.value = new L.FeatureGroup();
  map.value.addLayer(drawingLayer.value);

  // Initialize Leaflet-Geoman controls
  map.value.pm.addControls({
    position: 'topleft',
    drawCircle: false,
    drawCircleMarker: false,
    drawRectangle: false,
    drawPolyline: false,
    drawMarker: false,
    cutPolygon: false,
    rotateMode: false
  });

  // Handle map clicks for adding cameras
  map.value.on('click', (e: L.LeafletMouseEvent) => {
    if (!props.isAddingCamera) return;

    if (!pendingCamera) {
      // First click - place camera
      pendingCamera = {
        position: [e.latlng.lat, e.latlng.lng],
        height: 6,
        tilt: 0,
        fov: 110
      };
      
      // Show temporary marker
      temporaryMarker?.remove();
      temporaryMarker = L.marker([e.latlng.lat, e.latlng.lng], { 
        icon: cameraIcon.value,
        zIndexOffset: 1000
      }).addTo(map.value!);
    } else {
      // Second click - place target and emit camera
      emit('camera-added', {
        ...pendingCamera,
        target: [e.latlng.lat, e.latlng.lng],
        id: crypto.randomUUID(),
        isActive: true
      } as Camera);
      
      // Clear temporary marker and pending state
      temporaryMarker?.remove();
      temporaryMarker = null;
      pendingCamera = null;
    }
  });

  // Handle polygon creation and editing
  map.value.on('pm:create', (e: any) => {
    drawingLayer.value?.addLayer(e.layer);
  });

  map.value.on('pm:remove', (e: any) => {
    drawingLayer.value?.removeLayer(e.layer);
  });

  isMapInitialized.value = true;
}

onMounted(async () => {
  await nextTick();
  await initializeMap();
});

watch(() => props.cameras, async (newCameras) => {
  if (!map.value || !isMapInitialized.value) return;
  
  // Clear all camera layers
  for (const cameraId of cameraLayers.value.keys()) {
    clearCameraLayers(cameraId);
  }

  // Add updated markers and vision cones
  newCameras.forEach(camera => {
    const layers: L.Layer[] = [];
    
    const cameraMarker = L.marker(camera.position, { 
      icon: cameraIcon.value,
      draggable: true
    }).addTo(map.value!);
    layers.push(cameraMarker);
    
    const targetMarker = L.marker(camera.target, { 
      icon: targetIcon.value,
      draggable: true
    }).addTo(map.value!);
    layers.push(targetMarker);
    
    const visionCone = L.polygon(calculateVisionCone(camera), {
      color: camera.isActive ? '#18223B' : '#94a3b8',
      fillColor: camera.isActive ? '#18223B' : '#cbd5e1',
      fillOpacity: 0.2,
      weight: 2
    }).addTo(map.value!);
    layers.push(visionCone);

    const connectionLine = L.polyline([camera.position, camera.target], {
      color: '#F6A11B',
      weight: 2,
      dashArray: '5, 10',
      opacity: 0.8
    }).addTo(map.value!);
    layers.push(connectionLine);

    // Store layers for this camera
    cameraLayers.value.set(camera.id, layers);

    cameraMarker.on('dragend', (e) => {
      const marker = e.target;
      emit('camera-moved', camera.id, [marker.getLatLng().lat, marker.getLatLng().lng]);
    });

    targetMarker.on('dragend', (e) => {
      const marker = e.target;
      emit('target-moved', camera.id, [marker.getLatLng().lat, marker.getLatLng().lng]);
    });
  });
}, { deep: true });
</script>

<style>
.camera-icon, .target-icon {
  display: flex !important;
  align-items: center;
  justify-content: center;
  background: none !important;
}

/* Geoman toolbar customization */
.leaflet-pm-toolbar {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.2);
}

.leaflet-pm-toolbar .leaflet-pm-action {
  width: 30px;
  height: 30px;
  background-color: white;
  border: none;
  color: #18223B;
}

.leaflet-pm-toolbar .leaflet-pm-action:hover {
  background-color: #f8f9fa;
}

.leaflet-pm-toolbar .leaflet-pm-action.active {
  background-color: #e9ecef;
}

.leaflet-pm-toolbar .leaflet-pm-action.leaflet-pm-button-polygon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24' stroke='%2318223B' stroke-width='2' fill='none'%3E%3Cpath d='M3 7V5a2 2 0 0 1 2-2h2'/><path d='M17 3h2a2 2 0 0 1 2 2v2'/><path d='M21 17v2a2 2 0 0 1-2 2h-2'/><path d='M7 21H5a2 2 0 0 1-2-2v-2'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
  background-size: 20px;
}
</style>